/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
/**
 * any is allowed in this file cause also debug messages for the sw-debug component are placed here. needed to check if sw works as expected
 */
import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { RolloutDialogService } from '../../shared/rollout-dialog/rollout-dialog.service';
import {
  RolloutCompletionEvent,
  PollingStatusEvent,
  RolloutPollingConfig,
} from './rollout-polling-sw.interfaces';
import { getRolloutStatusFromResults } from '../../shared/rollout-dialog/store/rollout.helper';

@Injectable({
  providedIn: 'root',
})
export class RolloutPollingSwService implements OnDestroy {
  private serviceWorker: ServiceWorker | null = null;
  private rolloutCompletions$ = new Subject<RolloutCompletionEvent>();
  private pollingStatus$ = new Subject<PollingStatusEvent>();
  private swReady$ = new BehaviorSubject<boolean>(false);
  private rolloutDialogService = inject(RolloutDialogService);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  private activeRolloutConfigs = new Map<string, RolloutPollingConfig>();

  // BroadcastChannel for receiving notifications from Service Worker
  private rolloutChannel?: BroadcastChannel;

  // Service Worker version tracking
  private readonly EXPECTED_SW_VERSION = '1.4.0';
  private readonly EXPECTED_SW_SCOPE = '/planning/';
  private readonly OLD_SW_SCOPE = '/';
  private currentSwVersion?: string;
  private pongReceived = false;
  private versionCheckState: 'idle' | 'in-progress' | 'verified' = 'idle';
  private versionCheckId = 0;

  constructor() {
    this.initializeServiceWorker();
    this.initializeBroadcastChannel();

    // Check for orphaned rollouts after app refresh (but don't auto-restore)
    this.initializeService();

    // Listen to route changes to check version when navigating to /planning/
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.checkVersionIfNeeded();
    });

    // Keep the cached worker reference in sync with whoever is currently controlling the page.
    // Without this a SW activation in another tab/subdomain can leave our reference in 'redundant'
    // state, which silently drops every postMessage and produces a false version-mismatch alert.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        const newController = navigator.serviceWorker.controller;
        // Only update our cached reference if the new controller is actually our SW.
        // The ngsw (scope '/') may also trigger controllerchange events.
        if (newController?.scriptURL.includes('sw-rollout-polling')) {
          this.serviceWorker = newController;
        }
        // Allow re-validation after a controller swap.
        this.versionCheckState = 'idle';
        this.checkVersionIfNeeded();
      });
    }
  }

  private async initializeServiceWorker(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      await this.migrateFromOldServiceWorker();

      navigator.serviceWorker.addEventListener('message', (event) => {
        this.handleServiceWorkerMessage(event);
      });

      const registration = await navigator.serviceWorker.register('/sw-rollout-polling.js', {
        scope: this.EXPECTED_SW_SCOPE,
      });

      if (registration.installing) {
        this.serviceWorker = registration.installing;
        registration.installing.addEventListener('statechange', (event) => {
          const sw = event.target as ServiceWorker;
          if (sw.state === 'activated') {
            this.serviceWorker = sw;
            this.swReady$.next(true);
            this.checkVersionIfNeeded();
          }
        });
      } else if (registration.waiting) {
        this.serviceWorker = registration.waiting;
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        registration.waiting.addEventListener('statechange', (event) => {
          const sw = event.target as ServiceWorker;
          if (sw.state === 'activated') {
            this.serviceWorker = sw;
            this.swReady$.next(true);
            this.checkVersionIfNeeded();
          }
        });
      } else if (registration.active) {
        this.serviceWorker = registration.active;
        this.swReady$.next(true);
        // Check version if needed (on planning routes)
        this.checkVersionIfNeeded();
      }

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        }
      });
    } catch (error) {
      console.error('[SW Service] Registration failed:', error);
      this.swReady$.next(false);
    }
  }

  private async migrateFromOldServiceWorker(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        const scope = new URL(registration.scope).pathname;

        if (
          scope === this.OLD_SW_SCOPE &&
          registration.active?.scriptURL.includes('sw-rollout-polling')
        ) {
          console.warn(
            '[SW Migration] Old-scope SW found and unregistered. Scope:',
            scope,
            'Script:',
            registration.active?.scriptURL,
          );
          await registration.unregister();
          const message = this.translocoService.translate('serviceWorker.versionMismatch');
          alert(message);
          this.unregisterAndReload();
        }
      }
    } catch (error) {
      console.error('[SW Service] Migration error:', error);
    }
  }

  private initializeBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      this.rolloutChannel = new BroadcastChannel('rollout-updates');
      this.rolloutChannel.onmessage = (event) => {
        this.handleBroadcastMessage(event.data);
      };
    } catch (error) {
      console.error('[RolloutPollingSwService] BroadcastChannel init error:', error);
    }
  }

  private handleBroadcastMessage(message: any): void {
    const { type, data } = message;

    switch (type) {
      case 'ROLLOUT_COMPLETED':
        this.rolloutCompletions$.next(data);
        if (data.completedBranches && Array.isArray(data.completedBranches)) {
          for (const [rolloutId, config] of this.activeRolloutConfigs.entries()) {
            const isCompleted = data.completedBranches.some(
              (branch: { branchUid: string }) => branch.branchUid === config.branchUid,
            );
            if (isCompleted) {
              this.activeRolloutConfigs.delete(rolloutId);
            }
          }
        }
        break;

      case 'POLLING_STARTED':
      case 'POLLING_STOPPED':
      case 'POLLING_TIMEOUT':
      case 'POLLING_ERROR':
      case 'ALL_POLLING_STOPPED':
      case 'ROLLOUT_FULLY_COMPLETED':
        this.pollingStatus$.next({ type, rolloutId: data.rolloutId, data });
        break;
    }
  }

  private handleServiceWorkerMessage(event: MessageEvent): void {
    const { type, data } = event.data;
    switch (type) {
      case 'API_REQUEST':
        this.handleApiRequest(data);
        break;

      case 'REQUEST_STATUS_CHECK':
        this.handleStatusCheckRequest(data);
        break;

      case 'SW_VERSION':
        if (data?.version) {
          this.currentSwVersion = data.version;
        }
        break;

      case 'PONG':
        this.pongReceived = true;
        if (data?.swVersion && data?.expectedVersion) {
          this.currentSwVersion = data.swVersion;
          const match = data.swVersion === data.expectedVersion;
          if (match) {
            this.versionCheckState = 'verified';
          } else {
            console.warn(
              `[SW Health Check] Version mismatch! SW=${data.swVersion}, expected=${data.expectedVersion}`,
            );
            const message = this.translocoService.translate('serviceWorker.versionMismatch');
            alert(message);
            this.unregisterAndReload();
          }
        }
        break;

      case 'ACTIVE_ROLLOUTS':
        this.handleOrphanedRollouts(data.rollouts || []);
        break;

      case 'ROLLOUT_COMPLETED':
      case 'POLLING_STARTED':
      case 'POLLING_STOPPED':
      case 'POLLING_TIMEOUT':
      case 'POLLING_ERROR':
      case 'ALL_POLLING_STOPPED':
      case 'ROLLOUT_FULLY_COMPLETED':
        if (!this.rolloutChannel) {
          this.handleBroadcastMessage({ type, data });
        }
        break;
    }
  }

  /**
   * Test method to verify Service Worker communication
   */
  testCommunication(): void {
    this.sendMessage({ type: 'PING', data: { test: true, timestamp: Date.now() } });
  }

  /**
   * Start background polling for a specific rollout
   */
  startPolling(config: RolloutPollingConfig): Observable<boolean> {
    // Store configuration for status checks
    this.activeRolloutConfigs.set(config.rolloutId, config);

    this.sendMessage({
      type: 'START_POLLING',
      data: config,
    });

    // Return observable that emits when polling actually starts
    return this.pollingStatus$.pipe(
      filter((event) => event.type === 'POLLING_STARTED' && event.rolloutId === config.rolloutId),
      map(() => true),
    );
  }

  /**
   * Stop polling for a specific rollout
   */
  stopPolling(rolloutId: string): void {
    this.sendMessage({
      type: 'STOP_POLLING',
      data: { rolloutId },
    });
  }

  /**
   * Stop all background polling
   */
  stopAllPolling(): void {
    this.sendMessage({
      type: 'STOP_ALL_POLLING',
    });
  }

  /**
   * Clean up old rollouts on app startup (development helper)
   */
  private cleanupOldRolloutsOnStartup(): void {
    // Wait a bit for SW to be ready
    setTimeout(() => {
      if (this.isReady()) {
        this.stopAllPolling();

        // Clear our local configs too
        this.activeRolloutConfigs.clear();
      }
    }, 2000); // 2 second delay to ensure SW is ready
  }

  /**
   * Force Service Worker reload for debugging
   */
  async forceServiceWorkerReload(): Promise<void> {
    try {
      // Stop all current polling
      this.stopAllPolling();
      this.activeRolloutConfigs.clear();

      // Unregister only the rollout polling SW (not the Angular ngsw)
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (registration.active?.scriptURL.includes('sw-rollout-polling')) {
          await registration.unregister();
        }
      }

      // Mark as not ready immediately
      this.swReady$.next(false);
      this.serviceWorker = null;

      // Clear cache if needed
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
          if (cacheName.includes('rollout') || cacheName.includes('sw')) {
            await caches.delete(cacheName);
          }
        }
      }

      // Wait a moment then re-initialize
      setTimeout(async () => {
        await this.initializeServiceWorker();
      }, 2000);
    } catch (error) {
      console.error('❌ [SW Service] Failed to reload SW:', error);
    }
  }

  /**
   * Get the count of active rollout configurations in Angular service
   */
  getActiveRolloutCount(): number {
    return this.activeRolloutConfigs.size;
  }

  /**
   * Check if Angular service has a configuration for the given rollout ID
   */
  hasRolloutConfig(rolloutId: string): boolean {
    return this.activeRolloutConfigs.has(rolloutId);
  }

  /**
   * Get active rollout configurations (for debugging)
   */
  getActiveRollouts(): RolloutPollingConfig[] {
    return Array.from(this.activeRolloutConfigs.values());
  }

  sendMessage(message: any): boolean {
    // Target our registered rollout-polling SW specifically. We cannot blindly use
    // navigator.serviceWorker.controller because the Angular ngsw (scope '/') may be
    // the controller for all pages, and it doesn't understand our messages.
    const controller = navigator.serviceWorker?.controller;
    const isOurController = controller?.scriptURL.includes('sw-rollout-polling');

    const target: ServiceWorker | null =
      (isOurController ? controller : null) ??
      (this.serviceWorker && this.serviceWorker.state === 'activated' ? this.serviceWorker : null);

    if (!target) {
      return false;
    }
    target.postMessage(message);
    return true;
  }

  /**
   * Check Service Worker version if we're on a /planning/ route.
   * This method is called on route changes, SW activation, and controller changes.
   *
   * Guards against false positives:
   * - Only runs on /planning/ routes (where our SW is scoped)
   * - Awaits navigator.serviceWorker.ready so we don't ping before the SW is active
   * - Skips when no controller exists (page not yet controlled, e.g. right after hard refresh);
   *   the subsequent 'controllerchange' event will retry
   * - Deduplicates concurrent checks via versionCheckState
   */
  private async checkVersionIfNeeded(): Promise<void> {
    if (!window.location.pathname.startsWith('/planning/')) {
      return;
    }
    if (this.versionCheckState !== 'idle') {
      return;
    }
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      await navigator.serviceWorker.ready;
    } catch {
      return;
    }

    // If our rollout polling SW is not available yet, bail out silently.
    // The SW activation handler or controllerchange will trigger a retry.
    if (!this.serviceWorker || this.serviceWorker.state !== 'activated') {
      return;
    }

    this.checkVersion();
  }

  /**
   * Check Service Worker version using postMessage (wakes terminated SWs).
   *
   * Uses a three-state machine (idle | in-progress | verified) so that transient failures
   * (e.g. SW busy waking up) can be retried on the next route change / controller swap,
   * while a successful verification short-circuits further checks for the session.
   */
  private checkVersion(): void {
    this.versionCheckState = 'in-progress';
    this.pongReceived = false;
    const checkId = ++this.versionCheckId;

    const sent = this.sendMessage({
      type: 'PING',
      data: { expectedVersion: this.EXPECTED_SW_VERSION },
    });

    if (!sent) {
      // No reachable worker -> do NOT alert. Reset so a future event can retry.
      this.versionCheckState = 'idle';
      return;
    }

    // First attempt: wait 3 seconds for response
    setTimeout(() => {
      if (this.pongReceived || this.versionCheckId !== checkId) {
        console.warn(
          `[SW Health Check] Stale check discarded (checkId=${checkId}, current=${this.versionCheckId}, pong=${this.pongReceived})`,
        );
        return;
      }

      // Retry once - SW may have needed time to wake up
      const retrySent = this.sendMessage({
        type: 'PING',
        data: { expectedVersion: this.EXPECTED_SW_VERSION },
      });

      if (!retrySent) {
        this.versionCheckState = 'idle';
        return;
      }

      // Second attempt: wait another 3 seconds before alerting
      setTimeout(() => {
        if (this.pongReceived || this.versionCheckId !== checkId) {
          console.warn(
            `[SW Health Check] Stale retry discarded (checkId=${checkId}, current=${this.versionCheckId}, pong=${this.pongReceived})`,
          );
          return;
        }
        // Reset state so a later controllerchange can re-validate if the user dismisses.
        this.versionCheckState = 'idle';
        console.warn(
          '[SW Health Check] No response after retry - triggering version mismatch.',
          'Controller scriptURL:',
          navigator.serviceWorker?.controller?.scriptURL ?? 'none',
          'Cached SW state:',
          this.serviceWorker?.state ?? 'null',
        );
        const message = this.translocoService.translate('serviceWorker.versionMismatch');
        alert(message);
        this.unregisterAndReload();
      }, 3000);
    }, 3000);
  }

  /**
   * Unregister only the rollout polling service worker and reload the page
   * Used when a version mismatch is detected
   * Does NOT unregister the Angular ngsw to avoid breaking the PWA
   */
  private async unregisterAndReload(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        if (registration.active?.scriptURL.includes('sw-rollout-polling')) {
          await registration.unregister();
        }
      }
      window.location.reload();
    } catch (error) {
      console.error('[SW Version Check] Error during unregister:', error);
      window.location.reload();
    }
  }

  /**
   * Get current Service Worker version
   * Returns undefined if version hasn't been received yet
   */
  getServiceWorkerVersion(): string | undefined {
    return this.currentSwVersion;
  }

  /**
   * Get expected Service Worker version
   */
  getExpectedServiceWorkerVersion(): string {
    return this.EXPECTED_SW_VERSION;
  }

  /**
   * Check if Service Worker version matches expected version
   */
  isServiceWorkerVersionCorrect(): boolean {
    return this.currentSwVersion === this.EXPECTED_SW_VERSION;
  }

  /**
   * Restore polling state after app refresh by asking SW what it's currently polling
   */
  // private restorePollingStateAfterRefresh(): void {
  //   // Wait a bit for SW to be ready
  //   setTimeout(() => {
  //     if (this.isReady()) {
  //       // Ask Service Worker for its current polling state
  //       this.sendMessage({
  //         type: 'GET_ACTIVE_ROLLOUTS',
  //       });
  //     }
  //   }, 2000); // 2 second delay to ensure SW is ready
  // }

  /**
   * Initialize service - check for existing rollouts but don't auto-restore
   */
  private initializeService(): void {
    // Just wait for SW to be ready, don't auto-restore
    setTimeout(() => {
      if (this.swReady$.value) {
        this.sendMessage({
          type: 'GET_ACTIVE_ROLLOUTS',
        });
      } else {
        console.warn('⚠️ [SW Service] SW not ready after delay');
      }
    }, 2000);
  }

  /**
   * Reconcile rollouts reported by the Service Worker after a page refresh.
   *
   * The SW is the source of truth for in-flight rollouts (its state is persisted in the
   * caches storage and survives tab reloads). When a client reconnects, we:
   *
   * 1. Rehydrate `activeRolloutConfigs` from every rollout the SW is still tracking, so
   *    the fallback `REQUEST_STATUS_CHECK` path has a config to work with. Without this
   *    a refreshed tab would answer "unknown rollout" and we'd lose the snackbar.
   * 2. For rollouts with enough data, proactively check the backend once so that already
   *    completed rollouts can be resolved immediately (and removed from the SW) instead
   *    of waiting for the next 10s tick.
   */
  private handleOrphanedRollouts(swActiveRollouts: any[]): void {
    if (!swActiveRollouts || !Array.isArray(swActiveRollouts) || swActiveRollouts.length === 0) {
      return;
    }

    const orphanedRollouts = swActiveRollouts.filter(
      (rollout) => !this.activeRolloutConfigs.has(rollout.rolloutId),
    );

    if (orphanedRollouts.length === 0) {
      return;
    }

    console.warn(`⚠️ [SW Service] Rehydrating ${orphanedRollouts.length} rollouts from SW`);

    orphanedRollouts.forEach(async (rollout) => {
      if (!rollout.branchName || !rollout.branchUid || !rollout.asyncTaskUId) {
        // Incomplete data - tell SW to drop it.
        this.sendStatusUpdate(rollout.rolloutId, {
          hasCompletedRollouts: false,
          allCompleted: true,
          completedBranches: [],
          stillRunning: [],
        });
        return;
      }

      const config: RolloutPollingConfig = {
        rolloutId: rollout.rolloutId,
        asyncTaskUId: rollout.asyncTaskUId,
        branchUid: rollout.branchUid,
        branchName: rollout.branchName,
        apiConfig: rollout.apiConfig || {
          endpoint: `${window.location.origin}/user/me/asynctask/${rollout.asyncTaskUId}/logs`,
          queryParams: { onlyMilestones: 'true' },
        },
      };

      // Restore config first so any subsequent REQUEST_STATUS_CHECK from the SW can be
      // answered correctly by this tab.
      this.activeRolloutConfigs.set(rollout.rolloutId, config);

      try {
        const status = await this.checkRolloutStatusInternal(rollout.rolloutId, config);

        if (status.allCompleted) {
          // Rollout already finished in the backend - push the result into the SW so it
          // broadcasts ROLLOUT_COMPLETED and removes the entry. The snackbar subscription
          // on the broadcast will then fire normally.
          this.sendStatusUpdate(rollout.rolloutId, {
            hasCompletedRollouts: true,
            allCompleted: true,
            completedBranches: status.completedBranches,
            stillRunning: status.stillRunning,
          });
        }
        // If not completed yet, leave the config in place. The SW will drive further
        // polling (primary path: SW self-fetch; fallback path: our REQUEST_STATUS_CHECK
        // handler which now has the restored config).
      } catch (error) {
        console.error(
          `❌ [SW Service] Failed to check status of rehydrated rollout ${rollout.rolloutId}:`,
          error,
        );
        // Keep the config but don't stop polling - the SW's own fetch or a later tick
        // may still succeed.
      }
    });
  }

  /**
   * Internal method to check rollout status with provided config
   * Used for orphan rollout validation
   */
  private async checkRolloutStatusInternal(rolloutId: string, config: any): Promise<any> {
    try {
      // Use the same method as handleStatusCheckRequest
      const logsResponse = await this.rolloutDialogService.fetchAsyncTaskLogs(config.asyncTaskUId);
      // Check logs for completion status
      return this.checkLogsForCompletion(logsResponse, config);
    } catch (error) {
      console.error(`[SW Service] ❌ Status check failed for ${rolloutId}:`, error);
      throw error;
    }
  }

  /**
   * Observable for rollout completion events
   */
  onRolloutCompleted(): Observable<RolloutCompletionEvent> {
    return this.rolloutCompletions$.asObservable();
  }

  /**
   * Get completion events for a specific rollout
   */
  onRolloutCompleted$(rolloutId: string): Observable<RolloutCompletionEvent> {
    return this.onRolloutCompleted().pipe(filter((event) => event.rolloutId === rolloutId));
  }

  /**
   * Observable for polling status changes
   */
  onPollingStatus(): Observable<PollingStatusEvent> {
    return this.pollingStatus$.asObservable();
  }

  /**
   * Observable that emits when Service Worker is ready
   */
  onServiceWorkerReady(): Observable<boolean> {
    return this.swReady$.asObservable().pipe(filter((ready) => ready));
  }

  /**
   * Check if Service Worker is available and ready
   */
  isReady(): boolean {
    return this.swReady$.value && this.serviceWorker !== null;
  }

  /**
   * Create a rollout polling configuration for async task
   */
  createPollingConfig(
    rolloutId: string,
    asyncTaskUId: string,
    branchUid: string,
    branchName: string,
    apiEndpoint: string,
    authToken?: string,
    queryParams?: Record<string, string>,
  ): RolloutPollingConfig {
    return {
      rolloutId,
      asyncTaskUId,
      branchUid,
      branchName,
      apiConfig: {
        endpoint: apiEndpoint,
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` }),
        },
        queryParams,
      },
    };
  }

  /**
   * Handle status check requests from Service Worker
   * Now directly polls async task logs instead of relying on dialog
   */
  private async handleStatusCheckRequest(data: any): Promise<void> {
    const { rolloutId } = data;
    // Get stored rollout config from active rollouts
    // We need to store the config when startPolling is called
    const rolloutConfig = this.getStoredRolloutConfig(rolloutId);

    if (!rolloutConfig) {
      console.warn('[SW Service] ⚠️ No config found for rollout:', rolloutId);
      this.sendStatusUpdate(rolloutId, {
        hasCompletedRollouts: false,
        allCompleted: true, // Stop polling this unknown rollout
        completedBranches: [],
        stillRunning: [],
      });
      return;
    }

    try {
      // Directly poll async task logs using the rollout service
      const logsResponse = await this.rolloutDialogService.fetchAsyncTaskLogs(
        rolloutConfig.asyncTaskUId,
      );

      // Check logs for completion status
      const completionResult = this.checkLogsForCompletion(logsResponse, rolloutConfig);

      // Send status update back to Service Worker
      this.sendStatusUpdate(rolloutId, completionResult);
    } catch (error) {
      console.error('[SW Service] ❌ Error checking rollout status:', error);
      this.sendStatusUpdate(rolloutId, {
        hasCompletedRollouts: false,
        allCompleted: false,
        completedBranches: [],
        stillRunning: [],
      });
    }
  }

  /**
   * Get stored rollout configuration for a rollout ID
   */
  private getStoredRolloutConfig(rolloutId: string): RolloutPollingConfig | null {
    // We'll store configs when startPolling is called
    return this.activeRolloutConfigs.get(rolloutId) || null;
  }

  /**
   * Check if a branch is currently being actively polled by the Service Worker
   * @param branchUid - The UID of the branch to check
   * @returns true if the branch is actively being polled, false otherwise
   */
  public isBranchActivelyPolling(branchUid: string): boolean {
    for (const config of this.activeRolloutConfigs.values()) {
      if (config.branchUid === branchUid) {
        return true;
      }
    }
    return false;
  }

  /**
   * Send status update to Service Worker
   */
  public sendStatusUpdate(rolloutId: string, statusData: any): void {
    this.sendMessage({
      type: 'STATUS_UPDATE',
      data: {
        rolloutId,
        ...statusData,
      },
    });
  }

  /**
   * Handle API requests from Service Worker by proxying through the main thread
   */
  private async handleApiRequest(data: any): Promise<void> {
    const { messageId } = data;

    try {
      this.sendMessage({
        type: 'API_RESPONSE',
        data: {
          messageId,
          success: false,
          error: 'API proxy not yet implemented - using alternative approach',
        },
      });
    } catch (error) {
      console.error('[SW Service] API request failed:', error);

      // Send error response back to Service Worker
      this.sendMessage({
        type: 'API_RESPONSE',
        data: {
          messageId,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
    }
  }

  /**
   * Check async task logs for completion status
   */
  private checkLogsForCompletion(logsResponse: any, rolloutConfig: RolloutPollingConfig): any {
    if (
      !logsResponse?.result ||
      !Array.isArray(logsResponse.result) ||
      logsResponse.result.length === 0
    ) {
      return {
        hasCompletedRollouts: false,
        allCompleted: false,
        completedBranches: [],
        stillRunning: [
          { branchName: rolloutConfig.branchName, branchUid: rolloutConfig.branchUid },
        ],
      };
    }

    const logs = logsResponse.result;

    // Check each log for completion status
    for (const log of logs) {
      // Parse jsonMessage to check for completion
      if (log.jsonMessage) {
        try {
          const jsonData = JSON.parse(log.jsonMessage);

          if (jsonData.Completed === true) {
            // Use getRolloutStatusFromResults for accurate status detection
            const rolloutStatus = getRolloutStatusFromResults(
              jsonData.Successful,
              jsonData.Failed,
              log.type, // latestMilestone fallback
            );

            return {
              hasCompletedRollouts: true,
              allCompleted: true,
              completedBranches: [
                {
                  branchName: rolloutConfig.branchName,
                  branchUid: rolloutConfig.branchUid,
                  status: rolloutStatus, // Now returns SUCCESS/ERROR/WARNING properly
                  logResponse: jsonData,
                },
              ],
              stillRunning: [],
            };
          }
        } catch (error) {
          console.warn(
            `[SW Service] ⚠️ Failed to parse jsonMessage for ${rolloutConfig.branchName}:`,
            error,
          );
        }
      }

      // Also check LogType for terminal states (fallback)
      const logType = log.type;
      if (logType === 1 || logType === 2 || logType === 4) {
        // Succeeded, Failed, Aborted - use getRolloutStatusFromResults with milestone
        const rolloutStatus = getRolloutStatusFromResults(
          undefined, // no successful array
          undefined, // no failed array
          logType, // fall back to milestone
        );

        return {
          hasCompletedRollouts: true,
          allCompleted: true,
          completedBranches: [
            {
              branchName: rolloutConfig.branchName,
              branchUid: rolloutConfig.branchUid,
              status: rolloutStatus,
              milestone: logType,
            },
          ],
          stillRunning: [],
        };
      }
    }

    // No completion found, still running
    return {
      hasCompletedRollouts: false,
      allCompleted: false,
      completedBranches: [],
      stillRunning: [{ branchName: rolloutConfig.branchName, branchUid: rolloutConfig.branchUid }],
    };
  }

  /**
   * Cleanup when service is destroyed
   */
  ngOnDestroy(): void {
    if (this.rolloutChannel) {
      this.rolloutChannel.close();
    }

    this.rolloutCompletions$.complete();
    this.pollingStatus$.complete();
    this.swReady$.complete();
  }
}
